import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import StyleStep from '@/pages/customization/StyleStep';
import PreferencesStep from '@/pages/customization/PreferencesStep';
import MeasurementStep from '@/pages/customization/MeasurementStep';
import SareeBlouseGate from '@/pages/customization/SareeBlouseGate';
import ConsultationModal from '@/components/layout/ConsultationModal';

const STEPS = ['Style', 'Preferences', 'Measurement'];
const STORAGE_KEY = 'besq_customization';

// Ready-to-Wear Saree: filter out the Blouse-sectioned design fields / preferences
// unless the user has picked "with blouse" in the Step-00 gate.
function filterSaree(fields, preferences, slug) {
  if (slug !== 'ready-to-wear-saree') return fields;
  const withBlouse = preferences?.with_blouse?.value === 'yes';
  if (withBlouse) return fields;
  return (fields || []).filter(f => f.section !== 'Blouse');
}

function loadSavedState(slug) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.slug !== slug) return null;
    return data;
  } catch { return null; }
}

function saveState(slug, style, preferences, measurement, step) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ slug, style, preferences, measurement, step }));
}

export default function CategoryDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consultationOpen, setConsultationOpen] = useState(false);
  const [measurementCanProceed, setMeasurementCanProceed] = useState(false);
  const stepContentRef = useRef(null);

  // Restore saved state or start fresh
  const saved = loadSavedState(slug);
  const initialStep = parseInt(searchParams.get('step') || (saved?.step ?? 0), 10);

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [styleState, setStyleState] = useState(saved?.style || {});
  const [preferencesState, setPreferencesState] = useState(saved?.preferences || {});
  const [measurementState, setMeasurementState] = useState(saved?.measurement || { method: '' });
  const [activeSection, setActiveSection] = useState('');

  // Track previous completion state so auto-advance only fires on the transition from incomplete → complete
  const prevStyleComplete = useRef(Object.keys(saved?.style || {}).length > 0);
  const prevPrefsComplete = useRef(Object.keys(saved?.preferences || {}).length > 0);

  useEffect(() => {
    api.get(`/categories/${slug}`)
      .then((res) => setCategory(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  // Persist state to sessionStorage on every change
  useEffect(() => {
    if (slug) saveState(slug, styleState, preferencesState, measurementState, currentStep);
  }, [slug, styleState, preferencesState, measurementState, currentStep]);

  // Scroll to top of step content when step changes
  useEffect(() => {
    if (stepContentRef.current) {
      stepContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const isStepComplete = useCallback((step) => {
    if (step === 0) {
      if (!category?.design_fields?.length) return true;
      // "Others" category special case: 3 upload-only fields, AT LEAST 1 must be filled
      if (category.slug === 'others') {
        return category.design_fields.some((f) => !!styleState[f.field_key]);
      }
      // Saree: must answer the Step-00 blouse gate first
      if (category.slug === 'ready-to-wear-saree' && !preferencesState.with_blouse?.value) {
        return false;
      }
      const effective = filterSaree(category.design_fields, preferencesState, category.slug);
      return effective.every((f) => !!styleState[f.field_key]);
    }
    if (step === 1) {
      if (!category?.preferences_schema?.length) return true;
      const effective = filterSaree(category.preferences_schema, preferencesState, category.slug);
      return effective.filter((f) => {
        if (!f.required) return false;
        // Skip closing_with if corresponding closing_side is "na" or empty
        if (f.key.endsWith('closing_with') || f.key === 'closing_with') {
          const prefix = f.key.replace(/closing_with$/, '').replace(/_$/, '');
          const sideKey = prefix ? `${prefix}_closing_side` : 'closing_side';
          const sideVal = preferencesState[sideKey];
          if (!sideVal || sideVal === 'na') return false;
        }
        return true;
      }).every((f) => {
        const val = preferencesState[f.key];
        if (Array.isArray(val)) return val.length > 0;
        if (val && typeof val === 'object' && val.values) return val.values.length > 0;
        return val !== undefined && val !== '' && val !== null;
      });
    }
    if (step === 2) return !!measurementState.method;
    return true;
  }, [category, styleState, preferencesState, measurementState]);

  const getIncompleteFields = useCallback((step) => {
    if (step === 0 && category?.design_fields?.length) {
      if (category.slug === 'others') {
        const anyFilled = category.design_fields.some((f) => !!styleState[f.field_key]);
        return anyFilled ? [] : ['At least one sample photo (front, back or bottom)'];
      }
      if (category.slug === 'ready-to-wear-saree' && !preferencesState.with_blouse?.value) {
        return ['Attached Blouse (Yes / No)'];
      }
      const effective = filterSaree(category.design_fields, preferencesState, category.slug);
      return effective.filter((f) => !styleState[f.field_key]).map((f) => f.field_label);
    }
    if (step === 1 && category?.preferences_schema?.length) {
      const effective = filterSaree(category.preferences_schema, preferencesState, category.slug);
      return effective.filter((f) => {
        if (!f.required) return false;
        if (f.key.endsWith('closing_with') || f.key === 'closing_with') {
          const prefix = f.key.replace(/closing_with$/, '').replace(/_$/, '');
          const sideKey = prefix ? `${prefix}_closing_side` : 'closing_side';
          const sideVal = preferencesState[sideKey];
          if (!sideVal || sideVal === 'na') return false;
        }
        const val = preferencesState[f.key];
        if (Array.isArray(val)) return val.length === 0;
        if (val && typeof val === 'object' && val.values) return val.values.length === 0;
        return !val || val === '';
      }).map((f) => f.label);
    }
    if (step === 2 && !measurementState.method) return ['Measurement method'];
    return [];
  }, [category, styleState, preferencesState, measurementState]);

  // Auto-advance: only when step JUST became complete (was incomplete, now complete)
  useEffect(() => {
    if (!category) return;
    const nowComplete = isStepComplete(0);
    if (currentStep === 0 && nowComplete && !prevStyleComplete.current) {
      const timer = setTimeout(() => setCurrentStep(1), 400);
      prevStyleComplete.current = true;
      return () => clearTimeout(timer);
    }
    prevStyleComplete.current = nowComplete;
  }, [styleState, currentStep, isStepComplete, category]);

  useEffect(() => {
    if (!category) return;
    const nowComplete = isStepComplete(1);
    if (currentStep === 1 && nowComplete && !prevPrefsComplete.current) {
      const timer = setTimeout(() => setCurrentStep(2), 400);
      prevPrefsComplete.current = true;
      return () => clearTimeout(timer);
    }
    prevPrefsComplete.current = nowComplete;
  }, [preferencesState, currentStep, isStepComplete, category]);

  const goToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      return;
    }
    for (let i = currentStep; i < step; i++) {
      if (!isStepComplete(i)) {
        const missing = getIncompleteFields(i);
        toast.error(`Please complete: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`, { icon: <AlertCircle className="w-4 h-4" /> });
        setCurrentStep(i);
        return;
      }
    }
    setCurrentStep(step);
  };

  const goNext = () => {
    if (!isStepComplete(currentStep)) {
      const missing = getIncompleteFields(currentStep);
      toast.error(`Please complete: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`, { icon: <AlertCircle className="w-4 h-4" /> });
      return;
    }
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const canReview = measurementState.method && (measurementState.method !== 'saved_measurement' || measurementCanProceed);

  const goToReview = () => {
    if (!canReview) {
      if (measurementState.method === 'saved_measurement' && !measurementCanProceed) {
        toast.error('Please select a saved measurement profile, or choose Pickup Measurement or Reference Garment');
      } else {
        toast.error('Please select a measurement method');
      }
      return;
    }
    sessionStorage.setItem('besq_review', JSON.stringify({
      categoryId: category.id,
      categorySlug: category.slug,
      categoryName: category.name,
      style: styleState,
      preferences: preferencesState,
      measurement: measurementState,
      designFields: filterSaree(category.design_fields, preferencesState, category.slug),
      preferencesSchema: filterSaree(category.preferences_schema, preferencesState, category.slug),
    }));
    navigate('/review');
  };

  useEffect(() => {
    if (!category) return;
    if (currentStep === 0 && !activeSection && category.design_fields?.[0]?.field_key) {
      setActiveSection(category.design_fields[0].field_key);
    }
  }, [currentStep, category, activeSection]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="h-10 w-64 bg-secondary animate-pulse mb-6" />
        <div className="h-96 bg-secondary animate-pulse" />
      </div>
    );
  }

  if (!category) {
    return <div className="max-w-5xl mx-auto px-6 py-24 text-center"><h2 className="font-display text-3xl text-ink">Category not found</h2></div>;
  }

  const isSaree = category.slug === 'ready-to-wear-saree';
  const withBlousePref = preferencesState.with_blouse;
  const isBlouseAttached = withBlousePref?.value === 'yes';
  // For saree: force user to answer the blouse gate before proceeding out of Style step.
  const sareeGateAnswered = !isSaree || !!withBlousePref?.value;

  const designFields = filterSaree(category.design_fields, preferencesState, category.slug);
  const preferencesSchema = filterSaree(category.preferences_schema, preferencesState, category.slug);

  const nextStepLabel = currentStep < STEPS.length - 1 ? STEPS[currentStep + 1] : 'Review';

  const jumpToField = (fieldKey) => {
    if (currentStep !== 0) setCurrentStep(0);
    setActiveSection(fieldKey);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 60);
  };

  return (
    <div className="bg-background" data-testid="category-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr]">
        {/* ============ SIDEBAR (desktop) ============ */}
        <aside className="hidden lg:block sticky top-[72px] self-start h-[calc(100vh-72px)] overflow-y-auto border-r border-border py-14 px-11" data-testid="cust-sidebar">
          <div className="eyebrow mb-3" style={{ fontSize: 13, fontWeight: 600 }}>Customisation</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em] mb-10" style={{ fontSize: 52 }} data-testid="category-title">{category.name}</h1>

          {/* Main steps with nested Your Selections under Style */}
          <ul className="border-y border-border">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isDone = i < currentStep && isStepComplete(i);
              return (
                <li key={step} className={`relative border-t border-border first:border-t-0 ${isActive ? '' : ''}`}>
                  <button onClick={() => goToStep(i)}
                    className={`w-full py-4 flex items-center gap-4 text-left cursor-pointer ${isActive ? 'text-ink' : 'text-ink-muted hover:text-ink'}`}
                    data-testid={`step-${step.toLowerCase()}`}>
                    {isActive && <span className="absolute -left-11 top-4 bottom-4 w-[2px] bg-ink" />}
                    <span className="font-display text-gold" style={{ fontSize: 22, width: 32 }}>0{i + 1}{isDone && ' ✓'}</span>
                    <span className={`uppercase tracking-luxe text-[13px] ${isActive ? 'font-semibold' : 'font-medium'}`}>{step}</span>
                  </button>
                  {/* Nested "Your Selections" under Style step */}
                  {i === 0 && isActive && designFields.length > 0 && (
                    <div className="pb-6 pl-12" data-testid="sidebar-selections">
                      <div className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-3">Your Selections</div>
                      <div className="grid grid-cols-2 gap-2.5">
                        {designFields.map((f) => {
                          const sel = styleState[f.field_key];
                          const isActiveField = activeSection === f.field_key;
                          const src = sel?.value ? (sel.value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${sel.value}` : sel.value) : null;
                          return (
                            <button key={f.field_key} onClick={() => jumpToField(f.field_key)}
                              className="text-left group/cell" data-testid={`sidebar-cell-${f.field_key}`}>
                              <div className={`aspect-square bg-secondary relative overflow-hidden border ${isActiveField ? 'border-ink outline outline-1 outline-offset-2 outline-ink' : sel ? 'border-gold/70' : 'border-border'}`}>
                                {src ? (
                                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center text-center px-1 uppercase tracking-luxe text-[9px] font-semibold text-ink-muted">
                                    {isActiveField ? 'Choosing…' : 'Pending'}
                                  </div>
                                )}
                                {sel && !isActiveField && (
                                  <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-gold text-background flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={2} /></div>
                                )}
                              </div>
                              <div className={`text-center mt-1.5 uppercase tracking-luxe text-[9.5px] font-semibold ${isActiveField || sel ? 'text-ink' : 'text-ink-muted'}`}>{f.field_label}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Alternative path — Book Consultation */}
          <div className="mt-10 pt-8 border-t border-border">
            <div className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-3">Prefer a Stylist?</div>
            <p className="text-[13px] text-ink/75 leading-[1.6] mb-5">
              Skip the wizard — let our stylist visit you for a personalised consultation.
            </p>
            <button onClick={() => setConsultationOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-semibold py-3.5 transition-all duration-300"
              style={{ fontSize: 12 }} data-testid="book-consultation-btn">
              Book Consultation
            </button>
          </div>
        </aside>

        {/* ============ MOBILE HEADER + SELECTION STRIP ============ */}
        <div className="lg:hidden bg-cream-2 border-b border-border px-5 pt-6 pb-4">
          <div className="flex items-center justify-between mb-1">
            <div className="eyebrow" style={{ fontSize: 10 }}>Customisation</div>
            <button onClick={() => setConsultationOpen(true)} className="uppercase tracking-luxe text-[10px] font-semibold text-ink border-b border-ink pb-0.5" data-testid="mobile-book-consult">Consult</button>
          </div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 32 }}>{category.name}</h1>

          {/* Main step pills (all 3) */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1" data-testid="mobile-step-pills">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isDone = i < currentStep && isStepComplete(i);
              return (
                <button key={step} onClick={() => goToStep(i)}
                  className={`px-3 py-2 uppercase tracking-luxe text-[10px] font-semibold border whitespace-nowrap ${
                    isActive ? 'bg-ink text-background border-ink' : isDone ? 'bg-background border-gold text-ink' : 'bg-background border-border text-ink-muted'
                  }`}>
                  {step}{isDone && ' ✓'}
                </button>
              );
            })}
          </div>

          {/* Mobile Selection Strip — Style step only */}
          {currentStep === 0 && designFields.length > 0 && (
            <div data-testid="mobile-selection-strip">
              {/* Phone: justify-between distributes 3 cells across ~390px viewport nicely.
                  Tablet+: left-align with a generous gap so labels don't crowd each other. */}
              <div className="flex justify-between sm:justify-start gap-3 sm:gap-8 mt-4 overflow-x-auto pb-2">
                {designFields.map((f) => {
                  const sel = styleState[f.field_key];
                  const isActiveField = activeSection === f.field_key;
                  const src = sel?.value ? (sel.value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${sel.value}` : sel.value) : null;
                  return (
                    <button key={f.field_key} onClick={() => jumpToField(f.field_key)}
                      className="flex-shrink-0 w-[80px] sm:w-[104px] md:w-[128px] text-center" data-testid={`mobile-cell-${f.field_key}`}>
                      <div className={`w-full aspect-square bg-secondary relative overflow-hidden border ${isActiveField ? 'border-ink outline outline-1 outline-offset-2 outline-ink' : sel ? 'border-gold' : 'border-border'}`}>
                        {src ? (
                          <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-center px-1 uppercase tracking-luxe text-[8px] font-semibold text-ink-muted">
                            {isActiveField ? 'Choosing…' : 'Pending'}
                          </div>
                        )}
                        {sel && !isActiveField && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-gold text-background flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={2} /></div>
                        )}
                      </div>
                      {/* Label — wraps naturally on narrow phones (cell is 80px), stays on one line
                          from sm+ (cell ≥104px is enough for "FRONT NECK STYLE" at text-[9-10px]). */}
                      <div className={`mt-1.5 uppercase tracking-luxe text-[9px] sm:text-[10px] font-semibold sm:whitespace-nowrap ${isActiveField || sel ? 'text-ink' : 'text-ink-muted'}`}>{f.field_label}</div>
                    </button>
                  );
                })}
              </div>
              {/* Progress bar */}
              <div className="flex gap-1 mt-1">
                {designFields.map((f) => {
                  const sel = styleState[f.field_key];
                  const isActiveField = activeSection === f.field_key;
                  return <span key={f.field_key} className={`flex-1 h-[2px] ${sel ? 'bg-gold' : isActiveField ? 'bg-ink' : 'bg-border'}`} />;
                })}
              </div>
            </div>
          )}
        </div>

        {/* ============ MAIN CONTENT ============ */}
        <main className="px-5 lg:px-16 py-10 lg:py-16 pb-28" ref={stepContentRef}>
          {currentStep === 0 && (
            <>
              {isSaree && (
                <SareeBlouseGate
                  value={withBlousePref}
                  onChange={(v) => setPreferencesState(p => ({ ...p, with_blouse: v }))}
                />
              )}
              {sareeGateAnswered && designFields.length > 0 && (
                <StyleStep
                  designFields={designFields}
                  selections={styleState}
                  onChange={setStyleState}
                  activeSection={activeSection}
                  onActiveSectionChange={setActiveSection}
                />
              )}
            </>
          )}
          {currentStep === 1 && <PreferencesStep preferencesSchema={preferencesSchema} preferences={preferencesState} onChange={setPreferencesState} />}
          {currentStep === 2 && <MeasurementStep measurementState={measurementState} onChange={setMeasurementState} categorySlug={category.slug} onCanProceed={setMeasurementCanProceed} />}
        </main>
      </div>

      {/* ============ BOTTOM ACTION BAR ============ */}
      <div className="fixed bottom-0 left-0 lg:left-[340px] right-0 z-40 bg-background border-t border-border px-5 lg:px-16 py-3 lg:py-4 flex items-center justify-between gap-3">
        <button
          onClick={goBack}
          disabled={currentStep === 0}
          data-testid="step-back-btn"
          className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-semibold px-5 lg:px-8 py-3 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ink"
          style={{ fontSize: 11 }}
        >
          ← Back
        </button>
        <span className="hidden md:block flex-1 text-center uppercase tracking-luxe text-[11px] font-semibold text-ink-muted">Step {currentStep + 1} of {STEPS.length} · {STEPS[currentStep]}</span>
        {currentStep < STEPS.length - 1 ? (
          <button onClick={goNext} data-testid="step-next-btn" className="inline-flex items-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-semibold px-5 lg:px-8 py-3 transition-all duration-300" style={{ fontSize: 11 }}>
            {nextStepLabel} →
          </button>
        ) : (
          <button onClick={goToReview} disabled={!canReview} data-testid="review-btn" className="inline-flex items-center gap-2 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-semibold px-5 lg:px-8 py-3 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink disabled:hover:text-background" style={{ fontSize: 11 }}>
            Review →
          </button>
        )}
      </div>

      <ConsultationModal open={consultationOpen} onOpenChange={setConsultationOpen} categoryId={category.id} draftCustomization={{ style: styleState, preferences: preferencesState, measurement: measurementState }} />
    </div>
  );
}
